import { computed, signal, WritableSignal } from '@angular/core';
import { BackupFileDownloaderService, TextBackupFile } from 'src/app/shared/download';
import { FileSizeHelper } from 'src/app/shared/utils';
import { VisualCompareFileDescriptor } from 'src/app/shared/visual-compare-file-info/visual-compare-file-descriptor';

export class FileItem {
  private backupFile: TextBackupFile = null;

  constructor(
    public name: string,
    public fileType: string,
    public fileIdentifier: string,
    public groupKey: string = null
  ) { }

  get displayName(): string {
    return `${this.name}: ${this.filename}`;
  }

  get filename(): string {
    return this.backupFile?.FileName;
  }

  get contentType(): string {
    return this.backupFile?.ContentType;
  }

  get encoding(): string {
    return this.backupFile?.Encoding;
  }

  get bomPresent(): boolean {
    return this.backupFile?.BomPresent ?? false;
  }

  get size(): number {
    return this.backupFile?.FileSize;
  }

  get formattedSize(): string {
    return this.backupFile ? FileSizeHelper.humanFileSize(this.backupFile.FileSize) : '-';
  }

  get content(): string {
    return this.backupFile?.FileText;
  }

  async load(backupFileDownloader: BackupFileDownloaderService, includeText: boolean): Promise<void> {
    if (this.fileIdentifier && this.fileType && (!this.backupFile || (includeText && this.backupFile.FileText === null))) {
      this.backupFile = await backupFileDownloader.getFileText(this.fileIdentifier, this.fileType, includeText);
    }
  }
}

export class FileGroup {
  constructor(public key: string, public items: FileItem[]) { }
}

export type DropDownDataSource = FileGroup[];

export class DocumentInfo implements VisualCompareFileDescriptor {
  selectedFile: WritableSignal<FileItem>;
  content = computed(() => this.selectedFile()?.content);

  constructor(
    private backupFileDownloader: BackupFileDownloaderService,
    public documentId: number,
    public interchangeInId: number,
    public latestDocumentStatus: string,
    public dropDownDataSource: DropDownDataSource,
    autoSelectFirst = false
  ) {
    this.selectedFile = signal<FileItem>(null);
    if (autoSelectFirst) {
      this.selectFile(dropDownDataSource?.find(() => true)?.items?.find(() => true));
    }
  }

  get filename(): string {
    return this.selectedFile()?.filename ?? '';
  }

  get contentType(): string {
    return this.selectedFile()?.contentType ?? '';
  }

  get encoding(): string {
    return this.selectedFile()?.encoding ?? '';
  }

  get bomPresent(): boolean {
    return this.selectedFile()?.bomPresent ?? false;
  }

  get size(): number {
    return this.selectedFile()?.size ?? 0;
  }

  get formattedSize(): string {
    return this.selectedFile()?.formattedSize ?? '';
  }

  get labelText(): string {
    return this.documentId ? 'Document' : 'Interchange';
  }

  get idText(): string {
    return this.documentId ? `D-${this.documentId}` : `I-${this.interchangeInId}`;
  }

  async selectFile(file: FileItem | null): Promise<void> {
    await file?.load(this.backupFileDownloader, true);
    this.selectedFile.set(file);
  }
}
